import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Send, Paperclip, FileText, X } from "lucide-react";
import { api, apiError, fileUrl } from "../../lib/api";
import { fmtDateTime } from "../../lib/format";

const isImage = (name) => /\.(png|jpe?g|gif|webp|heic)$/i.test(name || "");

export default function Messages({ companyId, title = "Messages" }) {
  const [messages, setMessages] = useState([]);
  const [me, setMe] = useState(null);
  const [text, setText] = useState("");
  const [attach, setAttach] = useState(null); // {data, name}
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState([]);
  const [typingUser, setTypingUser] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimer = useRef(null);
  const lastTypeRef = useRef(0);
  const sendTyping = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const now = Date.now();
    if (now - lastTypeRef.current > 1500) { lastTypeRef.current = now; try { ws.send(JSON.stringify({ type: "typing" })); } catch { /* noop */ } }
  };

  const load = useCallback(async (markRead) => {
    if (!companyId) return;
    try {
      const { data } = await api.get(`/messages?company_id=${companyId}`);
      setMessages(data.messages); setMe(data.me);
      if (markRead && data.unread > 0) api.post(`/messages/read?company_id=${companyId}`).catch(() => {});
    } catch { /* ignore */ }
  }, [companyId]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  // Realtime updates via websocket (falls back to the 15s poll above if unavailable).
  useEffect(() => {
    if (!companyId) return undefined;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws;
    try {
      ws = new WebSocket(`${base}/api/ws/messages/${companyId}?token=${localStorage.getItem("cs_token")}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let d; try { d = JSON.parse(ev.data); } catch { return; }
        if (d.type === "message") load(true);
        else if (d.type === "presence") setOnline(d.users || []);
        else if (d.type === "typing") {
          setTypingUser(d.user?.name || "Someone");
          clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setTypingUser(null), 3000);
        }
      };
    } catch { /* polling covers it */ }
    return () => { try { if (ws) ws.close(); } catch { /* noop */ } };
  }, [companyId, load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File must be under 8MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setAttach({ data: reader.result, name: f.name });
    reader.readAsDataURL(f);
  };

  const send = async () => {
    if (!text.trim() && !attach) return;
    setSending(true);
    try {
      await api.post("/messages", { company_id: companyId, body: text.trim() || null,
        attachment_data: attach?.data || null, attachment_name: attach?.name || null });
      setText(""); setAttach(null); if (fileRef.current) fileRef.current.value = "";
      await load(true);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const othersOnline = online.some((u) => u.id !== me);
  return (
    <div className="flex flex-col h-[70vh] bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden" data-testid="messages-panel">
      <div className="px-5 py-3 border-b border-slate-800 font-semibold flex items-center justify-between">
        <span className="flex items-center gap-2"><Send className="w-4 h-4 text-blue-400" /> {title}</span>
        <span className="flex items-center gap-1.5 text-xs font-normal" data-testid="chat-presence">
          <span className={`w-2 h-2 rounded-full ${othersOnline ? "bg-emerald-400" : "bg-slate-600"}`} />
          <span className="text-slate-400">{othersOnline ? "Online" : "Offline"}</span>
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
        {messages.length === 0 && <p className="text-center text-slate-500 text-sm py-10">No messages yet. Start the conversation.</p>}
        {messages.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} data-testid="message-bubble" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-100"}`}>
                {!mine && <div className="text-[11px] font-semibold text-blue-300 mb-0.5">{m.sender_name} · {m.sender_role === "processor" ? "Processor" : m.sender_role === "super_admin" ? "Platform" : "Company"}</div>}
                {m.body && <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>}
                {m.attachment_path && (
                  isImage(m.attachment_name)
                    ? <a href={fileUrl(m.attachment_path)} target="_blank" rel="noreferrer"><img src={fileUrl(m.attachment_path)} alt={m.attachment_name} className="mt-2 rounded-lg max-h-48 border border-white/10" /></a>
                    : <a href={fileUrl(m.attachment_path)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-2 text-sm underline"><FileText className="w-4 h-4" /> {m.attachment_name}</a>
                )}
                <div className={`text-[10px] mt-1 ${mine ? "text-blue-100/70" : "text-slate-500"}`}>{fmtDateTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {attach && (
        <div className="px-4 py-2 border-t border-slate-800 flex items-center gap-2 text-sm text-slate-300">
          <FileText className="w-4 h-4" /> <span className="truncate flex-1">{attach.name}</span>
          <button onClick={() => { setAttach(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-rose-400"><X className="w-4 h-4" /></button>
        </div>
      )}
      {typingUser && <div className="px-4 py-1 text-xs text-slate-400 italic" data-testid="typing-indicator">{typingUser} is typing…</div>}
      <div className="p-3 border-t border-slate-800 flex items-center gap-2">
        <label className="cursor-pointer p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors">
          <Paperclip className="w-5 h-5 text-slate-300" />
          <input ref={fileRef} data-testid="message-attach-input" type="file" onChange={onFile} className="hidden" />
        </label>
        <input data-testid="message-input" value={text} onChange={(e) => { setText(e.target.value); sendTyping(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…" className="flex-1 rounded-xl bg-slate-950 border border-slate-700 px-4 py-2.5 focus:border-blue-500 outline-none transition" />
        <button data-testid="message-send-button" onClick={send} disabled={sending}
          className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-60 transition-colors"><Send className="w-5 h-5 text-white" /></button>
      </div>
    </div>
  );
}
